import { z } from 'zod'
import { AI_USE_CASES, type AIUseCase } from '@/lib/ai/langchain/types'

/**
 * Platform role presets per use case (ai_use_case_roles). An agent carries a SET
 * of them; applying COPIES the composed result into the agent's config storage —
 * roles are not a runtime resolution layer.
 */

/** The seeded generic starter role — default selection in agent creation. */
export const DEFAULT_ROLE_SLUG = 'custom'

export const ROLE_LABEL_MAX_LENGTH = 60

/**
 * Hard limit on a role's and a composed system prompt. Shared by the role CRUD schema
 * and the composition output check. The 6000-character
 * figure in the composition meta-prompt is a generation TARGET, not a check:
 * exceeding it is not an error and nothing truncates.
 */
export const ROLE_SYSTEM_PROMPT_MAX_LENGTH = 20000

/** A prompt stored before the limit stays savable as long as a save does not grow it. */
export function growsPastPromptLimit(next: string, stored: string | null | undefined, limit: number): boolean {
  return next.length > limit && next.length > (stored?.length ?? 0)
}

/** Roles an agent may carry at once. Above this the merged prompt stops being coherent. */
export const MAX_AGENT_ROLES = 3

/** Ceiling on the union of classifier labels — 3 roles x ~6 labels would be unusable. */
export const MAX_CLASSIFIER_LABELS = 10

const OTHER_LABEL = 'other'

/**
 * Union of the roles' classifier labels, in role order: case-insensitive dedupe
 * keeping the first spelling, `Other` forced last, total capped at
 * {@link MAX_CLASSIFIER_LABELS} with the `Other` slot reserved.
 *
 * Code owns this set, never the LLM: the labels are the KEYS per-category agent
 * settings hang on, so a non-deterministic set would silently drop state.
 */
export function composeClassifierLabels(
  presets: ReadonlyArray<{ settings?: Record<string, unknown> | null }>,
): string[] {
  const seen = new Set<string>()
  const labels: string[] = []
  let other: string | undefined

  for (const preset of presets) {
    const presetLabels = preset.settings?.classifier_labels
    if (!Array.isArray(presetLabels)) continue

    for (const raw of presetLabels) {
      if (typeof raw !== 'string') continue
      const label = raw.trim()
      if (!label) continue

      const key = label.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)

      if (key === OTHER_LABEL) other = label
      else labels.push(label)
    }
  }

  const capacity = other ? MAX_CLASSIFIER_LABELS - 1 : MAX_CLASSIFIER_LABELS
  const capped = labels.slice(0, capacity)
  return other ? [...capped, other] : capped
}

export type AgentRolesInputResult =
  | { ok: true; roles: string[] }
  | { ok: false; error: string }

/**
 * API-boundary check for `config.roles`: an array of 1..{@link MAX_AGENT_ROLES}
 * unique slugs, with `custom` exclusive (it is the "write your own" starter, so
 * combining it with a real role is meaningless). Returns the trimmed slugs in
 * input order — {@link normalizeRoleOrder} imposes the canonical one.
 */
export function parseAgentRolesInput(value: unknown): AgentRolesInputResult {
  if (!Array.isArray(value) || value.some((slug) => typeof slug !== 'string')) {
    return { ok: false, error: 'roles must be an array of role slugs' }
  }

  const roles = (value as string[]).map((slug) => slug.trim())
  if (roles.some((slug) => !slug)) {
    return { ok: false, error: 'roles must not contain empty slugs' }
  }
  if (!roles.length) {
    return { ok: false, error: 'At least one role is required' }
  }
  if (roles.length > MAX_AGENT_ROLES) {
    return { ok: false, error: `An agent can carry at most ${MAX_AGENT_ROLES} roles` }
  }
  if (new Set(roles).size !== roles.length) {
    return { ok: false, error: 'roles must not contain duplicates' }
  }
  if (roles.length > 1 && roles.includes(DEFAULT_ROLE_SLUG)) {
    return { ok: false, error: `The '${DEFAULT_ROLE_SLUG}' role cannot be combined with other roles` }
  }

  return { ok: true, roles }
}

/** The only state where a tenant-invented role label applies. */
export function isCustomOnly(roles: readonly string[]): boolean {
  return roles.length === 1 && roles[0] === DEFAULT_ROLE_SLUG
}

/**
 * Canonical role order: `sort_order` of the channel's presets, unknown slugs
 * last in input order. The user does not control priority, so the same SET of
 * roles must always produce the same composition input.
 */
export function normalizeRoleOrder(
  roles: readonly string[],
  presets: ReadonlyArray<{ slug: string; sort_order: number }>,
): string[] {
  const orderBySlug = new Map(presets.map((p) => [p.slug, p.sort_order]))
  return [...roles].sort((a, b) => {
    const orderA = orderBySlug.get(a)
    const orderB = orderBySlug.get(b)
    if (orderA !== undefined && orderB !== undefined && orderA !== orderB) return orderA - orderB
    if (orderA === undefined && orderB !== undefined) return 1
    if (orderB === undefined && orderA !== undefined) return -1
    // Equal (or both unknown) sort_order: fall back to the slug, never to input
    // order — the same SET must not depend on how the caller listed it.
    return a.localeCompare(b)
  })
}

/** Accepts absent, null (clear), or a bounded string — the API-boundary shape check. */
export function isValidRoleLabelInput(value: unknown): value is string | null | undefined {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.length <= ROLE_LABEL_MAX_LENGTH)
  )
}

/**
 * Trimmed custom-role display name. Meaningful only for an agent whose whole
 * role set is `['custom']`; any named preset resolves to null so a stale label
 * can't lie.
 */
export function resolveRoleLabel(
  label: string | null | undefined,
  roles: readonly string[] | undefined,
): string | null {
  if (!roles || !isCustomOnly(roles)) return null
  const trimmed = label?.trim()
  return trimmed ? trimmed : null
}

/** Agent channels that support role selection. */
export type AgentRoleScopeType = 'email_agent' | 'message_agent' | 'voice_agent'

/** Use cases a role application touches per channel; roles across use cases link by equal slugs. */
export const AGENT_ROLE_USE_CASES: Record<AgentRoleScopeType, readonly AIUseCase[]> = {
  email_agent: [AI_USE_CASES.EMAIL_AUTO_REPLY, AI_USE_CASES.EMAIL_CLASSIFICATION],
  message_agent: [AI_USE_CASES.MESSAGE_AGENT_REPLY],
  voice_agent: [AI_USE_CASES.VOICE_AGENT],
}

/** The use case whose roles populate the channel's role selector. */
export const AGENT_PRIMARY_ROLE_USE_CASE: Record<AgentRoleScopeType, AIUseCase> = {
  email_agent: AI_USE_CASES.EMAIL_AUTO_REPLY,
  message_agent: AI_USE_CASES.MESSAGE_AGENT_REPLY,
  voice_agent: AI_USE_CASES.VOICE_AGENT,
}

/** ai_use_case_roles row as consumed by UI and apply logic (snake_case for UI parity). */
export interface AgentRole {
  id: string
  use_case: string
  slug: string
  name: string
  description: string | null
  system_prompt: string
  settings: Record<string, unknown>
  is_system: boolean
  sort_order: number
}

/**
 * `settings` contract per use case. On apply, the validated settings object is
 * shallow-merged into the agent's scoped tenant_ai_configs.processing_config.
 * Use cases without an entry only carry a prompt (settings must be {}).
 */
export const classificationRoleSettingsSchema = z.object({
  classifier_labels: z
    .array(z.string().trim().min(1, 'Category cannot be empty.'))
    .min(1, 'At least one category is required.'),
})

export const ROLE_SETTINGS_SCHEMAS: Partial<Record<AIUseCase, z.ZodType<Record<string, unknown>>>> = {
  [AI_USE_CASES.EMAIL_CLASSIFICATION]: classificationRoleSettingsSchema,
}

const emptySettingsSchema = z.object({}).strict()

/** Validate a role's settings payload against its use case contract. */
export function roleSettingsSchemaFor(useCase: string): z.ZodType<Record<string, unknown>> {
  return ROLE_SETTINGS_SCHEMAS[useCase as AIUseCase] ?? emptySettingsSchema
}
